import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "./PublicHome";
import { api } from "../lib/api";
import { Search, MapPin, Briefcase, Loader2, Building2, AlertCircle } from "lucide-react";
import AdSlot from "../components/AdSlot";
import InFeedAd, { isAdBreak, useAdRegistry, AD_EVERY } from "../components/InFeedAd";

const INDUSTRIES = ["Construction & Skilled Trades", "Healthcare", "Technology", "Manufacturing & Logistics", "Hospitality & Retail"];

export default function PublicJobs() {
  const [q, setQ] = useState("");
  const [usState, setUsState] = useState("");
  const [arrangement, setArrangement] = useState("");
  const [empType, setEmpType] = useState("");
  const [industry, setIndustry] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [states, setStates] = useState([]);
  const adReg = useAdRegistry();

  useEffect(() => {
    api.get("/public/state-availability")
      .then(r => setStates((r.data.states || []).filter(s => s.status === "ACTIVE")))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setErr(false);
    try {
      const params = { page, page_size: 20 };
      if (q) params.q = q;
      if (usState) params.us_state = usState;
      if (arrangement) params.work_arrangement = arrangement;
      if (empType) params.employment_type = empType;
      if (industry) params.industry = industry;
      const { data } = await api.get("/public/jobs", { params });
      setRows(data.jobs); setTotal(data.total); setHasMore(data.has_more);
    } catch { setErr(true); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, usState, arrangement, empType, industry]);
  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNav/>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Public marketplace</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Find jobs on ConnectMyHub™</h1>
          <p className="text-sm text-slate-600 mt-1">Browse open opportunities for free. Create a candidate account only when you're ready to apply.</p>
        </div>

        <form onSubmit={onSearch} className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="relative md:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Keyword or job title" data-testid="jobs-search"
                    className="w-full pl-9 pr-3 h-10 rounded-md border border-slate-200 text-sm"/>
          </div>
          <select value={industry} onChange={e=>{setIndustry(e.target.value); setPage(1);}} data-testid="jobs-industry-filter"
                   className="h-10 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All industries</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select value={usState} onChange={e=>{setUsState(e.target.value); setPage(1);}} data-testid="jobs-state-filter"
                   className="h-10 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">Any state</option>
            {states.map(s => <option key={s.state_code} value={s.state}>{s.state}</option>)}
          </select>
          <select value={arrangement} onChange={e=>{setArrangement(e.target.value); setPage(1);}} data-testid="jobs-arrangement-filter"
                   className="h-10 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">Any arrangement</option>
            <option value="Remote">Remote</option>
            <option value="Hybrid">Hybrid</option>
            <option value="Onsite">On-site</option>
          </select>
          <select value={empType} onChange={e=>{setEmpType(e.target.value); setPage(1);}} data-testid="jobs-emptype-filter"
                   className="h-10 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">Any type</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
          </select>
          <button type="submit" data-testid="jobs-search-btn" className="md:col-span-6 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold">Search</button>
        </form>

        {/* Industry pages — Construction & Skilled Trades first */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="industry-pages">
          <button onClick={()=>{setIndustry(""); setPage(1);}} data-testid="industry-tab-all"
                  className={`h-8 px-3 rounded-full text-xs font-semibold border ${industry===""?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>All</button>
          {INDUSTRIES.map(i => (
            <button key={i} onClick={()=>{setIndustry(i); setPage(1);}} data-testid={`industry-tab-${i.replace(/\s|&/g,"")}`}
                    className={`h-8 px-3 rounded-full text-xs font-semibold border ${industry===i?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>{i}</button>
          ))}
        </div>

        {loading && (
          <div data-testid="jobs-loading" className="text-sm text-slate-500 inline-flex items-center gap-2 py-10 justify-center w-full"><Loader2 size={14} className="animate-spin"/> Loading opportunities…</div>
        )}
        {!loading && err && (
          <div data-testid="jobs-error" className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex gap-3"><AlertCircle size={16}/> Something went wrong loading jobs. Please try again.</div>
        )}
        {!loading && !err && rows.length === 0 && (
          <div data-testid="jobs-empty" className="text-center bg-white border border-slate-200 rounded-xl py-14">
            <div className="text-sm font-semibold text-slate-900">No matching opportunities right now</div>
            <p className="text-xs text-slate-500 mt-1">Try clearing filters — new roles are added regularly.</p>
          </div>
        )}
        {!loading && !err && rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map((j, idx) => (
              <>
                <Link key={j.public_job_id} to={`/jobs/${j.public_job_id}`} data-testid={`job-card-${j.public_job_id}`}
                     className="group block bg-white border border-slate-200 rounded-xl p-5 hover:border-sky-500 hover:shadow-sm transition">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600">{j.work_arrangement}{j.employment_type ? ` · ${j.employment_type}` : ""}</div>
                    <div className="font-display font-bold text-lg text-slate-900 leading-tight group-hover:text-sky-700 truncate">{j.job_title}</div>
                  </div>
                </div>
                {j.employer_name && (<div className="text-xs text-slate-600 flex items-center gap-1.5"><Building2 size={12}/> {j.employer_name}</div>)}
                <div className="text-xs text-slate-600 flex items-center gap-1.5 mt-1"><MapPin size={12}/> {j.location_display}</div>
                {j.compensation && (
                  <div className="text-xs text-slate-700 mt-1.5 font-semibold">{j.compensation.currency} {j.compensation.min?.toLocaleString()}–{j.compensation.max?.toLocaleString()} <span className="text-slate-400 font-normal">/ {j.compensation.period}</span></div>
                )}
                <div className="text-xs text-slate-500 line-clamp-2 mt-2">{j.public_description}</div>
              </Link>
              {isAdBreak(idx, rows.length) && (
                <div key={`ad-break-${idx}`} className="md:col-span-2">
                  {idx === AD_EVERY - 1 ? <AdSlot placement="jobs_list" onLoaded={id => id && adReg.current.push(id)}/> : <InFeedAd zone="JOBS_MARKETPLACE_PREMIUM" registry={adReg} geo={{ state: states.find(s => s.state === usState)?.state_code }}/>}
                </div>
              )}
              </>
            ))}
          </div>
        )}

        {(rows.length > 0 || page > 1) && (
          <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
            <div>{total} opportunities</div>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={()=>setPage(p=>Math.max(1,p-1))} data-testid="jobs-prev-page"
                       className="h-8 px-3 rounded border border-slate-300 disabled:opacity-40">Previous</button>
              <div>Page {page}</div>
              <button disabled={!hasMore} onClick={()=>setPage(p=>p+1)} data-testid="jobs-next-page"
                       className="h-8 px-3 rounded border border-slate-300 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </main>
      <PublicFooter/>
    </div>
  );
}
